import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useCharacterData, useProfileImage } from '../../../hooks/useGlobalChatState';
import { LiveImageStatus } from '../../../data/liveMessageData/liveMessage';
import { SidePanelContent } from '../../shared/SidePanel';
import '@/app/ui/scrollbar.css';

interface CharacterSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CharacterSidePanel({ isOpen, onClose }: CharacterSidePanelProps) {
  const sidePanelRef = useRef<HTMLDivElement>(null);
  const { character } = useCharacterData();
  const { loadingStatus: profileImageStatus, imageData: profileImageData } = useProfileImage();
  
  const presignedUrl = profileImageStatus === LiveImageStatus.LOADED ? profileImageData?.src : undefined;

  // Handle click outside to close side panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidePanelRef.current && !sidePanelRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (event: React.MouseEvent) => {
    const target = event.target as Element;
    
    // Don't close if clicking on the side panel content
    if (sidePanelRef.current && sidePanelRef.current.contains(target)) {
      return;
    }
    
    // Close if clicking on the backdrop
    onClose();
  };

  if (!character) return null;

  return (
    <>
      <div 
        className={`fixed inset-0 bg-black/20 flex items-end justify-end z-50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleBackdropClick}
      >
        <div 
          ref={sidePanelRef}
          className={`relative bg-[#1a1a1e] backdrop-blur-md border-l border-white/5 h-full w-full max-w-md transform transition-transform duration-300 ease-in-out flex flex-col ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Floating Close Button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 z-10 text-white/70 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="flex-1 min-h-0 overflow-y-auto dark-scrollbar">
            <div className="p-4 space-y-4">
              {/* Profile Image */}
              {presignedUrl && (
                <div className="flex justify-center">
                  <img
                    className="w-16 h-16 object-cover rounded-full border border-white/5"
                    alt="Avatar"
                    src={presignedUrl}
                  />
                </div>
              )}
            
              <SidePanelContent 
                onClose={onClose}
                className="space-y-4"
              />
            </div>
          </div>
        </div>
      </div>

    </>
  );
} 